import { useState } from 'react'
import Button from '../../Buttons'
import { useAuth } from '../../../contexts/Auth'
import { useDispatch } from 'react-redux'
import { setUserLoading } from '../../../redux/systemSlice'
import { setUser } from '@sentry/react'
import * as Sentry from '@sentry/react'
import { useCookies } from 'react-cookie'
import { usePostHog } from 'posthog-js/react'
import { isValidEmail } from '../../../helpers/utility'
import { signInWithGooglePopup } from '../../../firebase'
import Input from '../../Input'
import { LuMail, LuLock } from 'react-icons/lu'

const errorToText = {
	'email-already-in-use': 'Email already in use.',
	unknown: 'Unknown error, please try again.',
}

const SignupContent = ({
	setVariant,
	setModalOpen,
	premiumPath,
	setGoToCheckout,
}: {
	setVariant?: (variant: 'login' | 'signup') => void
	setModalOpen: (open: boolean) => void
	premiumPath?: boolean
	setGoToCheckout: (humanizerUpgrade: boolean) => void
}) => {
	const [emailInput, setEmailInput] = useState<string | undefined>('')
	const [passwordInput, setPasswordInput] = useState<string | undefined>('')
	const [error, setError] = useState<undefined | 'email-already-in-use' | 'unknown'>(undefined)
	const { signup, setCurrentUser } = useAuth() as any
	const [cookies, setCookie] = useCookies(['docs'])
	const dispatch = useDispatch()
	const posthog = usePostHog()

	async function createUser(uid: string, email: string) {
		if (!uid || !email) throw Error('Called /user/create/ with missing uid and/or email')
		const requestOptions = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				email,
				uid,
				existingDocs: cookies?.docs?.length > 0 ? cookies.docs : [],
			}),
		}
		fetch(process['env']['REACT_APP_API_ROOT'] + '/user/create/', requestOptions)
			.then((res) => res.json())
			.then((result) => {
				dispatch(setUser(result))
				setCookie('docs', JSON.stringify([]))
			})
			.catch((err) => Sentry.captureException(err))
	}

	const loginGoogleUser = async () => {
		const response = await signInWithGooglePopup()
		if (!response) return
		posthog.capture('signup', { method: 'google' })
		posthog.capture('user created', { method: 'google' })

		setCurrentUser(response.user)

		if (premiumPath) {
			setGoToCheckout(true)
		}
		setModalOpen(false)
	}

	async function submitForm() {
		try {
			const formattedEmailInput = emailInput?.toLowerCase() ?? ''
			const result = await signup(formattedEmailInput, passwordInput)
			setCookie('docs', [])
			dispatch(setUserLoading({ value: true }))
			await createUser(result.user.uid, formattedEmailInput)
			posthog.capture('signup', { method: 'email' })
			posthog.capture('user created', { method: 'google' })

			setCurrentUser(result.user)
			dispatch(setUserLoading({ value: false }))

			if (premiumPath) {
				setGoToCheckout(true)
				return
			}
			setModalOpen(false)
		} catch (error: any) {
			Sentry.captureException(error)
			if (error.code === 'auth/email-already-in-use') {
				setError('email-already-in-use')
			} else {
				setError('unknown')
			}
		}
	}

	return (
		<>
			<div className="flex flex-col gap-2">
				<div className="flex flex-col gap-2 md:mb-1">
					<h5 className="md:text-2xl text-xl text-center font-semibold">Sign up</h5>
					<div className="text-[16px] text-gray-600 text-center mb-0 font-medium">Create your account with Good AI</div>
				</div>
				<Button
					type="primary"
					className="w-full flex justify-center items-center border px-3 py-3 gap-3 border-gray-300 hover:bg-gray-100 bg-white rounded-md shadow mt-4"
					onClick={loginGoogleUser}
				>
					<div className="gsi-material-button-icon w-5">
						<svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
							<path
								fill="#EA4335"
								d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
							></path>
							<path
								fill="#4285F4"
								d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
							></path>
							<path
								fill="#FBBC05"
								d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
							></path>
							<path
								fill="#34A853"
								d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
							></path>
							<path fill="none" d="M0 0h48v48H0z"></path>
						</svg>
					</div>
					<div className="text-center font-bold">Continue with Google</div>
				</Button>
				<div className="h-0 text-center border border-b-0 border-gray-300 mt-6 border-t-2" />
				<div className="flex flex-col gap-0.5 mt-5">
					<label htmlFor="enter-email-addres" className="text-sm font-semibold mb-1">
						Email address
					</label>
					<Input
						value={emailInput ?? ''}
						onChange={(e) => {
							setEmailInput(e.target.value)
							setError(undefined)
						}}
						type="email"
						id="enter-email-address"
						placeholder="you@example.com"
						leftIcon={LuMail({ size: 18 })}
					/>
				</div>
				<div className="flex flex-col gap-0.5 mt-3 mb-5">
					<label htmlFor="password" className="text-sm font-semibold mb-1">
						Password
					</label>
					<Input
						value={passwordInput ?? ''}
						onChange={(e) => {
							setPasswordInput(e.target.value)
							setError(undefined)
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								submitForm()
							}
						}}
						type="password"
						id="password"
						placeholder="Password"
						leftIcon={LuLock({ size: 18 })}
					/>
				</div>
				{error && <div className="error">{errorToText[error]}</div>}
				<div className="flex flex-col gap-2">
					{
						<Button
							type="default"
							onClick={submitForm}
							className="w-full h-10 font-bold my-2"
							disabled={!emailInput || !passwordInput || passwordInput.length < 6 || !isValidEmail(emailInput)}
						>
							<span>Sign up</span>
						</Button>
					}
					<div>
						<Button
							type="ghost"
							className="text-sm mb-0 w-full"
							onClick={() => {
								setVariant && setVariant('login')
							}}
						>
							Or sign in instead
						</Button>
					</div>
				</div>
			</div>
		</>
	)
}

export default SignupContent
